import { getQueryStringValue } from '../../../../../hooks/useURLParams';

// ── Field option helpers ───────────────────────────────────────────────────

export const mapStringToOption = (value, labelsMap, fieldAliasMap = {}) => {
  const alias = fieldAliasMap?.[value];
  const label = (alias && labelsMap?.[alias]) || labelsMap?.[value] || value;
  return { value, label };
};

export const mapFieldArray = (fieldArray, labelsMap, fieldAliasMap = {}) => {
  if (!Array.isArray(fieldArray)) return fieldArray;
  return fieldArray.map(item =>
    typeof item === 'string' ? mapStringToOption(item, labelsMap, fieldAliasMap) : item
  );
};

export const mapFilterArray = (filterArray, labelsMap, fieldAliasMap = {}) => {
  if (!Array.isArray(filterArray)) return filterArray;
  return filterArray.map(filter => ({
    ...filter,
    member: filter.member
      ? typeof filter.member === 'string'
        ? mapStringToOption(filter.member, labelsMap, fieldAliasMap)
        : filter.member
      : filter.member,
    ...(['inDateRange', 'notInDateRange'].includes(filter.operator) &&
    filter.values?.length >= 2
      ? { startDate: filter.values[0], endDate: filter.values[1] }
      : {}),
  }));
};

export const extractValues = fieldArray => {
  if (!Array.isArray(fieldArray)) return [];
  return fieldArray
    .map(item => (typeof item === 'string' ? item : item?.value))
    .filter(Boolean);
};

// ── Filter processing ──────────────────────────────────────────────────────

export const processFilters = (filterArray, operator = 'and') => {
  if (!Array.isArray(filterArray) || !filterArray.length) return [];

  const processed = filterArray
    .filter(f => {
      if (!f?.member || !f?.operator) return false;
      if (['set', 'notSet'].includes(f.operator)) return true;
      if (['inDateRange', 'notInDateRange'].includes(f.operator)) {
        return (f.startDate && f.endDate) || f.values?.length >= 2;
      }
      return f.values && (Array.isArray(f.values) ? f.values.length > 0 : f.values);
    })
    .map(f => ({
      member: typeof f.member === 'string' ? f.member : f.member?.value || f.member,
      operator: f.operator,
      values:
        ['inDateRange', 'notInDateRange'].includes(f.operator) && f.startDate && f.endDate
          ? [f.startDate, f.endDate]
          : Array.isArray(f.values)
          ? f.values
          : f.values
          ? [f.values]
          : [],
    }));

  if (processed.length <= 1 || operator === 'and') return processed;
  if (operator === 'or') return [{ or: processed }];
  return processed;
};

// ── Query generation ───────────────────────────────────────────────────────

/**
 * Generate a Cube.js query from form state based on sections config.
 *
 * @param {object} formState   - react-final-form values
 * @param {Array}  sections    - builder sections config
 * @param {object} [options]   - { limit, timeDimension, fieldAliasMap }
 */
export const generateQueryFromFormState = (formState, sections, options = {}) => {
  const dimensions = [];
  const measures   = [];
  const filters    = [];

  sections.forEach(section => {
    const values = extractValues(formState[section.formField]);
    if (section.fieldType === 'measure') {
      measures.push(...values);
    } else {
      dimensions.push(...values);
    }

    const sectionFilters = formState[section.filtersField];
    const operator       = formState[`${section.formField}FilterOperator`] || 'and';
    if (sectionFilters) {
      filters.push(...processFilters(sectionFilters, operator));
    }
  });

  const query = {
    dimensions,
    measures,
    filters,
    limit: options.limit || 100,
    total: true,
  };

  // Include fieldAliasMap for translation in exports/scheduled reports
  if (options.fieldAliasMap && Object.keys(options.fieldAliasMap).length > 0) {
    query.fieldAliasMap = options.fieldAliasMap;
  }

  if (options.timeDimension && formState.dateRange) {
    const { startDate, endDate } = formState.dateRange;
    if (startDate || endDate) {
      const tf = { member: options.timeDimension };
      if (startDate && endDate) {
        tf.operator = 'inDateRange';
        tf.values   = [startDate, endDate];
      } else if (startDate) {
        tf.operator = 'afterDate';
        tf.values   = [startDate];
      } else {
        tf.operator = 'beforeDate';
        tf.values   = [endDate];
      }
      query.filters.push(tf);
    }
  }

  return query;
};

// ── URL state helpers ──────────────────────────────────────────────────────

export const getDefaultFormValues = sections => {
  const defaults = {};
  sections.forEach(s => {
    defaults[s.formField]                      = undefined;
    defaults[s.filtersField]                   = undefined;
    defaults[`${s.formField}FilterOperator`]   = 'and';
  });
  return defaults;
};

export const getInitialValuesFromURL = (sections, labelsMap, fieldAliasMap = {}) => {
  const raw = getQueryStringValue('form_state');
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      const mapped = { ...parsed };
      sections.forEach(s => {
        if (parsed[s.formField])  mapped[s.formField]  = mapFieldArray(parsed[s.formField], labelsMap, fieldAliasMap);
        if (parsed[s.filtersField]) mapped[s.filtersField] = mapFilterArray(parsed[s.filtersField], labelsMap, fieldAliasMap);
      });
      return mapped;
    } catch (e) {
      // ignore
    }
  }
  return getDefaultFormValues(sections);
};

// ── Clean form state for URL storage ──────────────────────────────────────

export const cleanFormStateForURL = (values, sections) => {
  const clean = { ...values };
  sections.forEach(s => {
    clean[s.formField] = extractValues(values[s.formField]);
    if (values[s.filtersField]) {
      clean[s.filtersField] = (values[s.filtersField] || []).map(f => ({
        ...f,
        member: typeof f.member === 'string' ? f.member : f.member?.value,
        values:
          f.startDate && f.endDate
            ? [f.startDate, f.endDate]
            : Array.isArray(f.values)
            ? f.values
            : f.values
            ? [f.values]
            : [],
      }));
    }
  });
  return clean;
};
